import { Location } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Acheminement } from 'src/app/core/_models/acheminement';
import { Energie } from 'src/app/core/_models/energie';
import { Journeys } from 'src/app/core/_models/journeys';
import { Transport } from 'src/app/core/_models/transport';
import { Trip } from 'src/app/core/_models/trip';
import { AcheminementService } from 'src/app/core/_services/acheminement.service';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { environment } from 'src/environments/environment';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogJourneyComponent } from '../shared/dialog-journey/dialog-journey.component';
import { DialogLeaveComponent } from '../shared/dialog-leave/dialog-leave.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';
import { DialogTripComponent } from '../shared/dialog-trip/dialog-trip.component';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';

@Component({
  selector: 'app-acheminement',
  templateUrl: './acheminement.component.html',
  styleUrls: ['./acheminement.component.scss']
})
export class AcheminementComponent implements OnInit {
  state: any = {
    perimeterId: null
  };

  currentItem!: Acheminement;
  list_transports!: Transport[];
  list_energies!: Energie[];
  form!: FormGroup;
  unSaved: boolean = false;
  isSaving: boolean = false;
  favoriteId?: string;
  fileToUpload?: File;
  justificationFilePath: string;

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private activeRoute: ActivatedRoute,
    private achemService: AcheminementService,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private transportService: TransportService,
    private versionService: VersionService,
    private energieService: EnergieService,
    private location: Location,
    private utilsService: UtilsService,
    private resourceListRefreshService: ResourceListRefreshService,
    private translate: TranslateService,
    private uploadService: UploadService,
    private spinnerService: SpinnerOverlayService,
    private labelI18n: LabelI18nService
  ) {
    this.justificationFilePath = environment.baseUrl + 'Documents/Justification/';
    if (history.state.perimeterId) {
      this.state.perimeterId = history.state.perimeterId;
    }
    this.versionService.getAll().subscribe((versions) => {
      this.transportService.getAll(undefined, versions?.[0]?.versionId).subscribe((t) => {
        this.list_transports = t;
      });
    });
    this.energieService.getAllForTransportation().subscribe((data) => {
      this.list_energies = data;
    });
  }

  async canDeactivate(): Promise<boolean> {
    if (this.unSaved) {
      const dialogRef = this.dialog.open(DialogLeaveComponent);

      var rep = await dialogRef.afterClosed().toPromise();
      if (rep) {
        return true;
      }

      return false;
    }
    return true;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      code: ['', Validators.required],
      displayName: ['', Validators.required],
      fileName: [],
      fileDescription: []
    });

    this.activeRoute.params.subscribe((params) => {
      const resourceId = params['id'];
      if (resourceId && resourceId != 0) {
        this.achemService.getAcheminement(resourceId).subscribe(
          (itm) => {
            this.initForm(this.form, itm);
          },
          (error) => {
            this.snackbarService.open(SnackbarTypes.error);
            this.router.navigate([SiteMapUrl.MenuAcheminement]);
          }
        );
      } else {
        this.initForm(this.form, {});
      }
    });
  }

  get formCrls() {
    return this.form.controls;
  }

  //Initialiser le formulaire
  initForm(form: FormGroup, currentItem: Acheminement) {
    this.currentItem = currentItem;
    if (this.currentItem && this.currentItem.resourceId) {
      this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
        this.favoriteId = res ? res.userFavoriteId : '';
      });
    }
    form.reset();
    form.patchValue(currentItem);
  }

  onDplicateAch(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 30, duplicate: (code: string) => this.achemService.duplicate(resourceId, code) } })
      .afterClosed()
      .subscribe((res) => {
        if (res !== undefined) this.majStore(SnackbarTypes.duplicate);
      });
  }

  //Pop-up de suppression
  onDeleteDialog(resourceId: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_acheminement') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteAcheminement(resourceId);
      }
    });
  }

  //Supprimer un acheminement
  deleteAcheminement(resourceId: string) {
    if (resourceId) {
      this.achemService.delete(resourceId).subscribe({
        next: (data) => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuAcheminement]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  majStore(snackbarMsgType: string) {
    this.achemService.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  //Cliquer sur le bouton enregistrer
  async clickSave(currentItem: Acheminement) {
    if (this.form.invalid || this.isSaving) {
      return;
    }
    if (this.hasTripsRatioError()) {
      this.snackbarService.showMessage(this.translate.instant('acheminement_error_trips_ratio_100'));
      return;
    }
    this.isSaving = true;
    if (currentItem == null || currentItem == undefined) {
      currentItem = {} as Acheminement;
    }
    currentItem.code = this.form.get('code')?.value;
    currentItem.displayName = this.form.get('displayName')?.value;
    currentItem.fileName = this.form.get('fileName')?.value;
    currentItem.fileDescription = this.form.get('fileDescription')?.value;

    const isUpdate = !!this.currentItem?.resourceId;

    let rep = await this.onUpdateOrSaveAch(currentItem);
    rep.subscribe(
      (data: Acheminement) => {
        this.isSaving = false;
        this.majStore(isUpdate ? SnackbarTypes.update : SnackbarTypes.add);
        this.currentItem = data;
        this.location.replaceState(`${SiteMapUrl.MenuAcheminement}/${data.resourceId}`);
        this.router.navigate([`${SiteMapUrl.MenuAcheminement}`, data.resourceId], { state: { resourceId: data.resourceId, perimeterId: this.state.perimeterId } });
      },
      (error) => {
        this.isSaving = false;
        this.snackbarService.openError(error);
        this.spinnerService.hide();
      }
    );
  }

  tripsTotalRatio(): number {
    return (this.currentItem?.trip ?? []).reduce((total, trip) => parseFloat((total + Number(trip.ratio ?? 0)).toFixed(8)), 0);
  }

  // Sans parcours, l'acheminement peut s'enregistrer ; dès le premier, les parts doivent faire 100 %
  hasTripsRatioError(): boolean {
    return !!this.currentItem?.trip?.length && Math.abs(this.tripsTotalRatio() - 100) > 1e-6;
  }

  //Sauvgarder ou modifier les données dans la base de données
  onUpdateOrSaveAch(currentItem: Acheminement) {
    return this.currentItem?.resourceId ? Promise.resolve(this.achemService.update(currentItem)) : Promise.resolve(this.achemService.add(currentItem));
  }

  //Créer un parcours
  addTrip(): void {
    const dialogRef = this.dialog.open(DialogTripComponent, {
      data: { displayName: null, ratio: null }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        if (!this.currentItem?.trip) {
          this.currentItem.trip = [];
        }
        this.currentItem.trip.splice(0, 0, { displayName: data.displayName, ratio: data.ratio });
      }
    });
  }

  //Supprimer un parcours
  deleteTrip(tripIndex: number) {
    //let i = this.currentItem.trip.findIndex(t => t.tripId == trip.tripId);
    if (this.currentItem.trip) this.currentItem.trip.splice(tripIndex, 1);
  }

  //Modifier un parcours
  updateTrip(trip: Trip, tripIndex: number) {
    const dialogRef = this.dialog.open(DialogTripComponent, {
      data: { displayName: trip?.displayName, ratio: trip?.ratio }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data && this.currentItem.trip) {
        this.currentItem.trip[tripIndex].displayName = data.displayName;
        this.currentItem.trip[tripIndex].ratio = data.ratio;
      }
    });
  }

  //Créer un trajet
  addJourney(tripIndex: number) {
    if (!this.list_transports) {
      return;
    }
    const dialogRef = this.dialog.open(DialogJourneyComponent, {
      data: {
        transports: this.list_transports,
        energies: this.list_energies
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data && this.currentItem?.trip) {
        if (!this.currentItem?.trip[tripIndex].journeys) {
          this.currentItem.trip[tripIndex].journeys = [];
        }

        this.currentItem?.trip[tripIndex].journeys?.splice(0, 0, {
          transportDisplayName: data.transportDisplayName,
          transportId: data.transportId,
          energyId: data.energyId,
          energyDisplayName: data.energyDisplayName,
          energyDescription: data.energieDescription,
          doubleCargo: data.doubleCargo,
          distance: data.distance
        });
      }
    });
  }

  //Supprimer un trajet
  deleteJourney(tripIndex: number, journeysIndex: number) {
    if (this.currentItem.trip) this.currentItem.trip[tripIndex].journeys?.splice(journeysIndex, 1);
  }

  //Modifier un trajet
  updateJourney(journey: Journeys, tripIndex: number) {
    if (!this.list_transports) {
      return;
    }
    const dialogRef = this.dialog.open(DialogJourneyComponent, {
      data: {
        transports: this.list_transports,
        energies: this.list_energies,
        transportDisplayName: journey.transportDisplayName,
        transportId: journey.transportId,
        energyDisplayName: journey.energyDisplayName,
        energieDescription: journey.energyDescription,
        energyId: journey.energyId,
        distance: journey.distance,
        doubleCargo: journey.doubleCargo
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data && this.currentItem.trip) {
        let currentJourney = this.currentItem.trip[tripIndex].journeys?.find((j) => j.journeyId == journey.journeyId);
        if (currentJourney) {
          currentJourney.transportDisplayName = data.transportDisplayName;
          currentJourney.transportId = data.transportId;
          currentJourney.energyDisplayName = data.energyDisplayName;
          currentJourney.energyDescription = data.energieDescription;
          currentJourney.energyId = data.energyId;
          currentJourney.doubleCargo = data.doubleCargo;
          currentJourney.distance = data.distance;
        }
      }
    });
  }

  numberFormatted(numberToFormat?: number) {
    return Number(numberToFormat).toLocaleString(this.labelI18n.locale);
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then((res) => {
        this.favoriteId = '';
      });
    }
  }

  uploadProof(resourceId?: string, files?: any) {
    if (!resourceId || !files || files.length === 0) {
      return;
    }

    this.fileToUpload = <File>files[0];
    const formData = new FormData();
    formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload.name.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.form.get('fileDescription')?.setValue(event?.body);
          this.form.get('fileName')?.setValue(this.fileToUpload?.name);
          this.fileInput.nativeElement.value = '';
        }
      },
      () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFile() {
    this.fileInput.nativeElement.value = '';
    this.form.get('fileName')?.reset();
    this.form.get('fileDescription')?.reset();
  }
}
